/** Choosing the AI you practise with — the one place that choice is made.
 *
 * It used to be made on every screen that could ask the AI anything, which
 * put six model cards above a one-line answer on the lesson player, the
 * exam player and the speaking page. The decision never warranted that
 * much room, and on a phone it was a wall the learner scrolled past before
 * every question.
 *
 * Here it reads as what it is: a preference, next to the voice and the
 * interface language, set once. What the AI screens keep is a single line
 * naming the partner and its price, linking back to this card.
 *
 * Saves on click rather than behind a Save button, exactly like the voice
 * picker beside it. The whole interaction is one tap, and a second tap to
 * confirm it would be the larger half of the work.
 */
import { useEffect, useState } from "react";
import { doubtModels } from "../../api/tutor";
import { updateProfile } from "../../api/auth";
import type { AiModel } from "../../api/exams";
import { ApiError } from "../../api/http";
import { Card } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";

/** Paise → "₹0.22". Two decimals always: the cheapest partner costs a
 *  fifth of a rupee, and "₹0" beside "₹10.56" would read as free rather
 *  than cheap, losing the twenty-fold difference this card exists to show. */
const perConversation = (minor: number) =>
  `₹${(minor / 100).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export function PartnerSection() {
  const t = useT();
  const { push } = useToast();
  const [models, setModels] = useState<AiModel[]>([]);
  const [chosen, setChosen] = useState<string | null>(null);
  const [active, setActive] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    doubtModels()
      .then((r) => {
        if (cancelled) return;
        setModels(r.models);
        setActive(r.active_model ?? null);
      })
      // A deployment we cannot read the partners of simply shows no card.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Nothing to choose between is not a setting. Most deployments run one
  // vendor key, and a card offering a single option is furniture.
  if (models.length < 2) return null;

  const save = async (id: string | null) => {
    setBusy(true);
    try {
      // "" rather than null: the API reads an absent field as "leave this
      // alone", so clearing needs a value that means cleared.
      const me = await updateProfile({ ai_model_preference: id ?? "" });
      setChosen(me.ai_model_preference);
      // Re-read what the server will ACTUALLY use. Asking for a partner
      // whose vendor key has since gone silently falls back, and the line
      // on every other screen would otherwise name a model that is not
      // answering.
      const r = await doubtModels();
      setActive(r.active_model ?? null);
      push("success", t("profile.partnerSaved"));
    } catch (e) {
      push("error", e instanceof ApiError ? e.message : t("admin.credits.failed"));
    } finally {
      setBusy(false);
    }
  };

  // Which row reads as selected: an explicit choice if there is one, else
  // whatever the server resolved, so the card always shows a learner who
  // has never chosen what they are in fact getting.
  const selected = chosen ?? active;

  return (
    <Card title={t("profile.partner")}>
      <p className="text-sm text-muted">{t("profile.partnerHint")}</p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {models.map((m) => {
          const isOn = selected === m.id;
          return (
            <button
              key={m.id}
              type="button"
              disabled={busy}
              onClick={() => save(m.id)}
              aria-pressed={isOn}
              className={`rounded-control border p-3 text-left disabled:opacity-50 ${
                isOn ? "border-brand bg-brand-soft" : "border-line hover:border-brand"
              }`}
            >
              <span className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-bold text-ink">{m.label}</span>
                <span className="text-sm font-extrabold text-brand-dark tabular-nums">
                  {perConversation(m.price_minor)}
                </span>
              </span>
              <span className="mt-0.5 block text-xs text-muted">
                {t("partner.perConversation")}
              </span>
              <span className="mt-1 block text-xs text-muted">{m.blurb}</span>
            </button>
          );
        })}
      </div>
      {/* A way back to "I never chose". Without it the only escape from a
          choice is knowing which option was the default, which no screen
          says. */}
      <button
        type="button"
        disabled={busy || chosen === null}
        onClick={() => save(null)}
        className="mt-3 text-xs font-semibold text-brand-dark underline underline-offset-2 disabled:opacity-50 disabled:no-underline"
      >
        {t("profile.partnerDefault")}
      </button>
    </Card>
  );
}
